import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Form, Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import {
  FaBoxOpen,
  FaBoxes,
  FaClipboardCheck,
  FaEye,
  FaFileUpload,
  FaPlus,
  FaRedoAlt,
  FaSearch,
  FaTrash,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { isManagementUser, readStoredAdminUser } from './adminAccessControl';
import {
  POS_API_BASE_URL,
  buildApiErrorMessage,
  buildFetchErrorMessage,
  buildAuthHeaders,
  fetchScopedOutlets,
  formatCurrency,
  formatDateForInput,
  formatShortDateTime,
  getInitialScopedOutlets,
  getInitials,
  getItemTypeBadgeClass,
  getItemTypeLabel,
  getSyncStatusBadgeClass,
  getSyncStatusLabel,
  isStockTrackedProduct,
  normalizePagedRecords,
  parseJsonResponse,
  resolveAdminSessionToken,
} from './posBackofficeHelpers';
import './PosCatalogManager.css';

const DOCUMENT_CONFIG = {
  purchase: {
    key: 'purchase',
    title: 'Purchases',
    eyebrow: 'Catalog & Inventory',
    description:
      'Catat pembelian inventory POS langsung dari backoffice agar stok, HPP, dan jurnal pembelian tetap sinkron.',
    icon: FaBoxes,
    createUrl: 'api/purchase/',
    listUrl: 'api/purchase/reports',
    detailUrl: null,
    dateField: 'purchase_date',
    noteTitle: 'Flow pembelian inventory',
    noteCopy:
      'Gunakan halaman ini untuk stok masuk resmi outlet. Jika pembelian dibayar tempo, sistem akan otomatis membentuk hutang vendor agar pelunasannya bisa dilanjutkan dari menu Vendors.',
    workflow: [
      {
        label: '1. Tentukan supplier',
        value: 'Isi supplier, invoice, tanggal, lalu pilih metode bayar tunai atau tempo.',
      },
      {
        label: '2. Masukkan item',
        value: 'Tambahkan hanya product stok yang benar-benar dibeli outlet, lalu isi qty beli, unit beli, dan konversinya ke satuan stok dasar.',
      },
      {
        label: '3. Posting dokumen',
        value: 'Setelah disimpan, stok dasar, HPP per unit stok, dan ledger akan ikut terbarui. Jika tempo, hutang vendor juga langsung terbentuk.',
      },
      {
        label: '4. Hindari dokumen ganda',
        value: 'Jika ini tagihan expense, asset, atau rent, jangan pakai Purchase. Gunakan Vendor Bill agar tidak bentrok dengan workflow hutang inventory.',
      },
    ],
  },
  'stock-adjustment': {
    key: 'stock-adjustment',
    title: 'Stock Adjustment',
    eyebrow: 'Catalog & Inventory',
    description:
      'Koreksi stok manual untuk kehilangan, temuan fisik, kerusakan, atau penyesuaian inventory yang tidak berasal dari purchase.',
    icon: FaBoxOpen,
    createUrl: 'api/stock-adjustments/',
    listUrl: 'api/stock-adjustments/',
    detailUrl: id => `api/stock-adjustments/${id}`,
    dateField: 'adjustment_date',
    noteTitle: 'Flow koreksi stok',
    noteCopy:
      'Gunakan penyesuaian hanya untuk koreksi. Nilai selisih akan dicatat ke ledger dan, jika ada valuasi, jurnal accounting akan ikut diposting.',
    workflow: [
      {
        label: '1. Pilih tanggal koreksi',
        value: 'Gunakan tanggal real saat selisih stok diketahui.',
      },
      {
        label: '2. Isi delta stok',
        value: 'Nilai minus mengurangi stok, nilai plus menambah stok.',
      },
      {
        label: '3. Simpan alasan',
        value: 'Catatan yang jelas akan memudahkan audit kehilangan atau kerusakan.',
      },
    ],
  },
  'stock-opname': {
    key: 'stock-opname',
    title: 'Stock Opname',
    eyebrow: 'Catalog & Inventory',
    description:
      'Cocokkan stok sistem dengan stok fisik. Selisih akan dihitung otomatis dan menjadi dasar koreksi inventory outlet.',
    icon: FaClipboardCheck,
    createUrl: 'api/stock-opnames/',
    listUrl: 'api/stock-opnames/',
    detailUrl: id => `api/stock-opnames/${id}`,
    dateField: 'opname_date',
    noteTitle: 'Flow opname stok',
    noteCopy:
      'Masukkan stok fisik aktual per produk. Sistem akan menghitung selisih dibanding stok sistem, memperbarui stok, dan mencatat ledger opname.',
    workflow: [
      {
        label: '1. Pilih tanggal opname',
        value: 'Gunakan tanggal saat hitung fisik dilakukan di outlet.',
      },
      {
        label: '2. Isi stok aktual',
        value: 'Bandingkan stok fisik dengan stok sistem untuk setiap product.',
      },
      {
        label: '3. Posting hasil opname',
        value: 'Selisih akan langsung membentuk koreksi inventory dan ledger opname.',
      },
    ],
  },
};

const createPurchaseLine = () => ({
  product_id: '',
  quantity: '1',
  purchase_unit: '',
  purchase_conversion_qty: '1',
  purchase_price: '',
});

const createAdjustmentLine = () => ({
  product_id: '',
  quantity_delta: '',
  note: '',
});

const createOpnameLine = () => ({
  product_id: '',
  actual_stock: '',
  note: '',
});

const createInitialForm = mode => {
  const today = formatDateForInput(new Date());
  if (mode === 'purchase') {
    return {
      supplier_name: '',
      invoice_number: '',
      purchase_date: today,
      due_date: '',
      payment_method: 'cash',
      note: '',
      lines: [createPurchaseLine()],
    };
  }
  if (mode === 'stock-adjustment') {
    return {
      adjustment_date: today,
      reason: 'manual_adjustment',
      note: '',
      lines: [createAdjustmentLine()],
    };
  }
  return {
    opname_date: today,
    note: '',
    lines: [createOpnameLine()],
  };
};

const escapeHtml = value =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const buildIsoDate = value =>
  value ? new Date(`${value}T00:00:00`).toISOString() : new Date().toISOString();

const formatInventoryUnitLabel = value => String(value || '').trim().toUpperCase() || '-';

const PURCHASE_MATCH_STOPWORDS = new Set([
  'gr',
  'gram',
  'kg',
  'g',
  'ml',
  'ltr',
  'liter',
  'pcs',
  'pc',
  'pack',
  'item',
  'x',
]);

const normalizePurchaseMatchText = value =>
  String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .map(token => token.trim())
    .filter(token => token && !PURCHASE_MATCH_STOPWORDS.has(token))
    .join(' ')
    .trim();

const splitPurchaseMatchTokens = value =>
  normalizePurchaseMatchText(value)
    .split(/\s+/)
    .filter(Boolean);

const computeLevenshteinDistance = (left, right) => {
  const source = String(left || '');
  const target = String(right || '');
  if (!source) {
    return target.length;
  }
  if (!target) {
    return source.length;
  }

  const previous = Array.from({ length: target.length + 1 }, (_, index) => index);
  const current = new Array(target.length + 1).fill(0);

  for (let sourceIndex = 0; sourceIndex < source.length; sourceIndex += 1) {
    current[0] = sourceIndex + 1;
    for (let targetIndex = 0; targetIndex < target.length; targetIndex += 1) {
      const cost = source[sourceIndex] === target[targetIndex] ? 0 : 1;
      current[targetIndex + 1] = Math.min(
        current[targetIndex] + 1,
        previous[targetIndex + 1] + 1,
        previous[targetIndex] + cost
      );
    }
    for (let targetIndex = 0; targetIndex <= target.length; targetIndex += 1) {
      previous[targetIndex] = current[targetIndex];
    }
  }

  return previous[target.length];
};

const computePurchaseMatchScore = (sourceValue, targetValue) => {
  const source = normalizePurchaseMatchText(sourceValue);
  const target = normalizePurchaseMatchText(targetValue);
  if (!source || !target) {
    return 0;
  }
  if (source === target) {
    return 1;
  }

  const sourceTokens = splitPurchaseMatchTokens(source);
  const targetTokens = splitPurchaseMatchTokens(target);
  const sourceTokenSet = new Set(sourceTokens);
  const targetTokenSet = new Set(targetTokens);
  const overlapCount = sourceTokens.filter(token => targetTokenSet.has(token)).length;
  const longestTokenLength = Math.max(sourceTokens.length, targetTokens.length, 1);
  const tokenScore = overlapCount / longestTokenLength;
  const editScore = 1 - (computeLevenshteinDistance(source, target) / Math.max(source.length, target.length, 1));
  const containsScore = source.includes(target) || target.includes(source) ? 0.9 : 0;
  const firstTokenBonus =
    sourceTokens[0] && targetTokens[0] && sourceTokens[0] === targetTokens[0]
      ? 0.08
      : 0;
  const uniqueOverlapBonus =
    overlapCount >= 2 && sourceTokenSet.size >= 2 && targetTokenSet.size >= 2
      ? 0.05
      : 0;

  return Math.max(
    containsScore,
    Math.min(editScore * 0.58 + tokenScore * 0.42 + firstTokenBonus + uniqueOverlapBonus, 1)
  );
};

const getPurchaseMatchConfidence = score => {
  if (score >= 0.9) {
    return 'high';
  }
  if (score >= 0.78) {
    return 'medium';
  }
  if (score >= 0.62) {
    return 'low';
  }
  return 'unmatched';
};

const getPurchaseMatchBadgeClass = confidence => {
  switch (confidence) {
    case 'high':
      return 'pos-catalog-badge pos-catalog-badge--success';
    case 'medium':
      return 'pos-catalog-badge pos-catalog-badge--info';
    case 'low':
      return 'pos-catalog-badge pos-catalog-badge--warning';
    case 'manual':
      return 'pos-catalog-badge pos-catalog-badge--info';
    default:
      return 'pos-catalog-badge pos-catalog-badge--danger';
  }
};

const getPurchaseMatchLabel = confidence => {
  switch (confidence) {
    case 'high':
      return 'Match tinggi';
    case 'medium':
      return 'Match sedang';
    case 'low':
      return 'Match rendah';
    case 'manual':
      return 'Dipilih manual';
    default:
      return 'Perlu pilih manual';
  }
};

const dedupeMessages = values =>
  Array.from(new Set((Array.isArray(values) ? values : []).map(value => String(value || '').trim()).filter(Boolean)));

const findBestPurchaseProductMatch = (description, products) => {
  const normalizedDescription = normalizePurchaseMatchText(description);
  if (!normalizedDescription || !Array.isArray(products) || products.length === 0) {
    return null;
  }

  let bestMatch = null;
  products.forEach(product => {
    const nameScore = computePurchaseMatchScore(normalizedDescription, product?.name || '');
    const codeScore = computePurchaseMatchScore(normalizedDescription, product?.code || '');
    const score = Math.max(nameScore, codeScore);
    if (!bestMatch || score > bestMatch.score) {
      bestMatch = {
        product,
        score,
        confidence: getPurchaseMatchConfidence(score),
      };
    }
  });

  return bestMatch;
};

const flattenPurchaseProductOptions = options =>
  (Array.isArray(options) ? options : []).flatMap(optionGroup => (
    Array.isArray(optionGroup?.options) ? optionGroup.options : [optionGroup]
  ));

const truncatePurchasePreviewText = (value, limit = 44) => {
  const normalized = String(value || '').trim();
  if (normalized.length <= limit) {
    return normalized;
  }

  return `${normalized.slice(0, Math.max(limit - 1, 1)).trimEnd()}…`;
};

const buildPurchaseProductSearchText = product =>
  normalizePurchaseMatchText([
    product?.name,
    product?.code,
    product?.barcode,
    product?.purchase_unit,
    product?.satuan,
    product?.item_type,
  ].join(' '));

const buildPurchaseProductOptionMeta = (product, description) => {
  const scanText = String(description || '').trim();
  const score = scanText
    ? Math.max(
        computePurchaseMatchScore(scanText, product?.name || ''),
        computePurchaseMatchScore(scanText, product?.code || ''),
        computePurchaseMatchScore(scanText, product?.barcode || '')
      )
    : 0;

  return {
    ocrScore: score,
    ocrConfidence: getPurchaseMatchConfidence(score),
  };
};

const sortPurchaseProductOptions = (left, right) => {
  const leftScore = Number(left?.ocrScore || 0);
  const rightScore = Number(right?.ocrScore || 0);
  if (rightScore !== leftScore) {
    return rightScore - leftScore;
  }

  return String(left?.label || '').localeCompare(String(right?.label || ''), 'id', {
    sensitivity: 'base',
  });
};

const buildPurchaseProductSelectOptions = (productOptions, description) => {
  const baseOptions = (Array.isArray(productOptions) ? productOptions : []).map(option => {
    const meta = buildPurchaseProductOptionMeta(option?.product, description);
    return {
      ...option,
      ...meta,
    };
  }).filter(option => option.value);

  const scanText = String(description || '').trim();
  if (!scanText) {
    return [...baseOptions].sort((left, right) =>
      String(left.label).localeCompare(String(right.label), 'id', { sensitivity: 'base' })
    );
  }

  const rankedOptions = [...baseOptions].sort(sortPurchaseProductOptions);
  const recommendedOptions = rankedOptions.slice(0, Math.min(6, rankedOptions.length));
  const recommendedIds = new Set(recommendedOptions.map(option => option.value));
  const remainingOptions = rankedOptions.filter(option => !recommendedIds.has(option.value));

  return [
    {
      label: `Rekomendasi OCR • ${truncatePurchasePreviewText(scanText)}`,
      options: recommendedOptions,
    },
    {
      label: 'Semua product stok',
      options: remainingOptions,
    },
  ].filter(optionGroup => Array.isArray(optionGroup.options) && optionGroup.options.length > 0);
};

const findPurchaseProductOptionByValue = (options, value) =>
  flattenPurchaseProductOptions(options).find(option => option.value === String(value || '')) || null;

const filterPurchaseProductOption = (candidate, inputValue) => {
  const normalizedInput = normalizePurchaseMatchText(inputValue);
  if (!normalizedInput) {
    return true;
  }

  const option = candidate?.data || {};
  const searchableText = String(option.searchText || '');
  if (searchableText.includes(normalizedInput)) {
    return true;
  }

  return computePurchaseMatchScore(normalizedInput, searchableText) >= 0.52;
};

const purchaseProductSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    borderRadius: '14px',
    borderColor: state.isFocused ? 'rgba(217, 255, 99, 0.34)' : 'var(--border)',
    backgroundColor: 'var(--surface-alt)',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'rgba(217, 255, 99, 0.28)',
    },
  }),
  valueContainer: base => ({
    ...base,
    padding: '0.2rem 0.7rem',
  }),
  input: base => ({
    ...base,
    color: 'var(--text-strong)',
  }),
  placeholder: base => ({
    ...base,
    color: 'var(--text-muted)',
  }),
  singleValue: base => ({
    ...base,
    color: 'var(--text-strong)',
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 2056,
  }),
  menu: base => ({
    ...base,
    zIndex: 2056,
    borderRadius: '16px',
    overflow: 'hidden',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--surface-elevated)',
    boxShadow: '0 18px 38px rgba(15, 23, 34, 0.24)',
  }),
  menuList: base => ({
    ...base,
    paddingBlock: '0.35rem',
  }),
  groupHeading: base => ({
    ...base,
    paddingInline: '0.9rem',
    color: 'var(--text-muted)',
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.04em',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'rgba(217, 255, 99, 0.18)'
      : state.isFocused
        ? 'rgba(255, 255, 255, 0.08)'
        : 'transparent',
    color: 'var(--text-strong)',
    padding: '0.7rem 0.9rem',
  }),
  indicatorSeparator: base => ({
    ...base,
    backgroundColor: 'var(--border)',
  }),
  dropdownIndicator: base => ({
    ...base,
    color: 'var(--text-muted)',
  }),
  clearIndicator: base => ({
    ...base,
    color: 'var(--text-muted)',
  }),
  noOptionsMessage: base => ({
    ...base,
    color: 'var(--text-muted)',
  }),
};

function PosInventoryDocumentManager({ mode = 'purchase' }) {
  const config = DOCUMENT_CONFIG[mode] || DOCUMENT_CONFIG.purchase;
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const canAccess = isManagementUser(currentUser);
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const initialOutlets = getInitialScopedOutlets(currentUser);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState(initialOutlets);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId || initialOutlets[0]?.id || '');
  const [products, setProducts] = useState([]);
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState(() => createInitialForm(config.key));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState({
    start_date: formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 30))),
    end_date: formatDateForInput(new Date()),
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [invoiceScanState, setInvoiceScanState] = useState({
    status: 'idle',
    error: '',
    data: null,
  });

  const selectedOutletName = useMemo(
    () => outlets.find(outlet => outlet.id === selectedOutlet)?.name || 'Belum dipilih',
    [outlets, selectedOutlet]
  );

  const stockProducts = useMemo(
    () => products.filter(product => isStockTrackedProduct(product)),
    [products]
  );

  const productMap = useMemo(
    () => stockProducts.reduce((accumulator, product) => {
      accumulator[String(product.id)] = product;
      return accumulator;
    }, {}),
    [stockProducts]
  );
  const purchaseProductSourceOptions = useMemo(
    () => stockProducts.map(product => ({
      value: String(product?.id || ''),
      label: String(product?.name || '').trim() || `Product #${product?.id || '-'}`,
      product,
      searchText: buildPurchaseProductSearchText(product),
    })).filter(option => option.value),
    [stockProducts]
  );
  const purchaseProductMenuPortalTarget = typeof document !== 'undefined' ? document.body : null;

  const resetForm = useCallback(() => {
    setFormData(createInitialForm(config.key));
    setInvoiceScanState({
      status: 'idle',
      error: '',
      data: null,
    });
  }, [config.key]);

  const openCreateModal = useCallback(() => {
    resetForm();
    setShowCreateModal(true);
  }, [resetForm]);

  const closeCreateModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const fetchProducts = useCallback(async (token, outletId) => {
    if (!outletId || !POS_API_BASE_URL) {
      setProducts([]);
      return;
    }

    const params = new URLSearchParams({
      outlet_id: outletId,
      page: '1',
      limit: '500',
    });

    const response = await fetch(`${POS_API_BASE_URL}api/products/?${params.toString()}`, {
      headers: buildAuthHeaders(token),
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat product POS.'));
    }

    const { records: fetched } = normalizePagedRecords(data);
    setProducts(Array.isArray(fetched) ? fetched : []);
  }, []);

  const fetchRecords = useCallback(async (token, outletId) => {
    if (!outletId || !POS_API_BASE_URL) {
      setRecords([]);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const params = new URLSearchParams({
        outlet_id: outletId,
      });
      if (dateRange.start_date) {
        params.set('start_date', dateRange.start_date);
      }
      if (dateRange.end_date) {
        params.set('end_date', dateRange.end_date);
      }
      const response = await fetch(`${POS_API_BASE_URL}${config.listUrl}?${params.toString()}`, {
        headers: buildAuthHeaders(token),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, `Gagal memuat ${config.title}.`));
      }

      const { records: fetched } = normalizePagedRecords(data);
      setRecords(Array.isArray(fetched) ? fetched : []);
    } catch (error) {
      console.error(`Failed to fetch POS ${config.key}:`, error);
      setRecords([]);
      setErrorMessage(error.message || `Gagal memuat ${config.title}.`);
    } finally {
      setLoading(false);
    }
  }, [config.key, config.listUrl, config.title, dateRange.end_date, dateRange.start_date]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    let active = true;

    const initialize = async () => {
      try {
        const token = await resolveAdminSessionToken(verifyAndRefreshToken, navigate);
        if (!active || !token) {
          return;
        }

        setAuthToken(token);
        const scopedOutlets = await fetchScopedOutlets({ token, currentUser });
        if (!active) {
          return;
        }

        setOutlets(scopedOutlets);
        setSelectedOutlet(prev => prev || queryOutletId || scopedOutlets[0]?.id || '');
      } catch (error) {
        console.error(`Failed to initialize POS ${config.key}:`, error);
        if (active) {
          setErrorMessage(error.message || `Gagal menyiapkan halaman ${config.title}.`);
        }
      }
    };

    initialize();
    return () => {
      active = false;
    };
  }, [canAccess, config.key, config.title, currentUser, navigate, queryOutletId, verifyAndRefreshToken]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchProducts(authToken, selectedOutlet).catch(error => {
      console.error(`Failed to load POS products for ${config.key}:`, error);
      setProducts([]);
      setErrorMessage(error.message || 'Gagal memuat product POS.');
    });
  }, [authToken, config.key, fetchProducts, selectedOutlet]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchRecords(authToken, selectedOutlet);
  }, [authToken, fetchRecords, selectedOutlet]);

  useEffect(() => {
    resetForm();
  }, [resetForm, selectedOutlet]);

  const filteredRecords = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) {
      return records;
    }

    return records.filter(row => {
      const haystacks =
        config.key === 'purchase'
          ? [row.id, row.invoice_number, row.supplier_name, row.note, row.payment_method, row.payment_status]
          : [row.id, row.reason, row.note, row.status];
      return haystacks.some(value => String(value || '').toLowerCase().includes(keyword));
    });
  }, [config.key, records, search]);

  const summary = useMemo(() => {
    const synced = filteredRecords.filter(row => String(row.accounting_sync_status || '').toLowerCase() === 'synced').length;
    const pending = filteredRecords.filter(row => String(row.accounting_sync_status || '').toLowerCase() === 'pending').length;
    const failed = filteredRecords.filter(row => String(row.accounting_sync_status || '').toLowerCase() === 'failed').length;
    const totalAmount = filteredRecords.reduce((sum, row) => {
      if (config.key === 'purchase') {
        return sum + Number(row.total || 0);
      }
      return sum;
    }, 0);

    return {
      totalDocuments: filteredRecords.length,
      synced,
      pending,
      failed,
      totalAmount,
      partnerCount:
        config.key === 'purchase'
          ? new Set(
              filteredRecords
                .map(row => String(row.supplier_name || '').trim())
                .filter(Boolean)
            ).size
          : 0,
    };
  }, [config.key, filteredRecords]);

  const lines = useMemo(
    () => (Array.isArray(formData.lines) ? formData.lines : []),
    [formData.lines]
  );

  const computedTotal = useMemo(() => {
    if (config.key !== 'purchase') {
      return 0;
    }

    return lines.reduce((sum, line) => {
      const qty = Number(line.quantity || 0);
      const price = Number(line.purchase_price || 0);
      return sum + qty * price;
    }, 0);
  }, [config.key, lines]);

  const addLine = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      lines: [
        ...(Array.isArray(prev.lines) ? prev.lines : []),
        config.key === 'purchase'
          ? createPurchaseLine()
          : config.key === 'stock-adjustment'
            ? createAdjustmentLine()
            : createOpnameLine(),
      ],
    }));
  }, [config.key]);

  const removeLine = useCallback(index => {
    setFormData(prev => {
      const nextLines = (Array.isArray(prev.lines) ? prev.lines : []).filter((_, lineIndex) => lineIndex !== index);
      return {
        ...prev,
        lines: nextLines.length > 0 ? nextLines : [
          config.key === 'purchase'
            ? createPurchaseLine()
            : config.key === 'stock-adjustment'
              ? createAdjustmentLine()
              : createOpnameLine(),
        ],
      };
    });
  }, [config.key]);

  const handleFieldChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLineChange = useCallback((index, field, value) => {
    setFormData(prev => {
      const nextLines = (Array.isArray(prev.lines) ? prev.lines : []).map((line, lineIndex) => {
        if (lineIndex !== index) {
          return line;
        }

        const nextLine = {
          ...line,
          [field]: value,
        };

        if (field === 'product_id') {
          const selectedProduct = productMap[String(value)];
          if (config.key === 'purchase' && selectedProduct) {
            const conversionQty = Math.max(Number(selectedProduct.purchase_conversion_qty || 1), 1);
            nextLine.purchase_unit = String(selectedProduct.purchase_unit || selectedProduct.satuan || '');
            nextLine.purchase_conversion_qty = String(conversionQty);
            if (!String(line.purchase_price || '').trim()) {
              nextLine.purchase_price = String(Number(selectedProduct.last_purchase_price || 0) * conversionQty);
            }
            if (String(line.extracted_description || '').trim()) {
              nextLine.match_confidence = 'manual';
              nextLine.matched_product_name = String(selectedProduct.name || '');
            }
          }
          if (config.key === 'stock-opname' && selectedProduct && nextLine.actual_stock === '') {
            nextLine.actual_stock = String(selectedProduct.stock ?? '');
          }
        }

        return nextLine;
      });

      return {
        ...prev,
        lines: nextLines,
      };
    });
  }, [config.key, productMap]);

  const hydrateExtractedInvoicePreview = useCallback(rawResult => {
    const extractedItems = Array.isArray(rawResult?.items) ? rawResult.items : [];
    const previewItems = extractedItems.map(item => {
      const quantity = Math.max(Number(item?.quantity || 0), 1);
      const purchasePrice = Math.max(
        Number(item?.purchase_price || 0),
        0
      );
      const lineTotal = Math.max(
        Number(item?.total || 0),
        purchasePrice > 0 ? quantity * purchasePrice : 0
      );
      const bestMatch = findBestPurchaseProductMatch(item?.description, stockProducts);
      const matchedProduct = bestMatch?.confidence !== 'unmatched' ? bestMatch?.product : null;
      const conversionQty = Math.max(Number(matchedProduct?.purchase_conversion_qty || 1), 1);

      return {
        description: String(item?.description || '').trim(),
        barcode: String(item?.barcode || '').trim(),
        quantity,
        purchase_price: purchasePrice,
        total: lineTotal,
        product_id: matchedProduct ? String(matchedProduct.id) : '',
        matched_product_name: matchedProduct?.name || bestMatch?.product?.name || '',
        purchase_unit: String(matchedProduct?.purchase_unit || matchedProduct?.satuan || '').trim().toUpperCase(),
        purchase_conversion_qty: conversionQty,
        match_confidence: bestMatch?.confidence || 'unmatched',
        match_score: Number(bestMatch?.score || 0),
      };
    });

    const unmatchedCount = previewItems.filter(item => !item.product_id).length;
    const matchedCount = previewItems.length - unmatchedCount;
    const warnings = dedupeMessages([
      ...(Array.isArray(rawResult?.warnings) ? rawResult.warnings : []),
      unmatchedCount > 0
        ? `${unmatchedCount} item belum menemukan product yang cukup yakin. Cek lalu pilih manual sebelum simpan.`
        : '',
    ]);

    return {
      provider: String(rawResult?.provider || '').trim(),
      supplier_name: String(rawResult?.supplier_name || '').trim(),
      invoice_number: String(rawResult?.invoice_number || '').trim(),
      purchase_date: String(rawResult?.purchase_date || '').trim(),
      payment_method: String(rawResult?.payment_method || '').trim().toLowerCase(),
      total: Number(rawResult?.total || 0),
      raw_text: String(rawResult?.raw_text || ''),
      warnings,
      items: previewItems,
      matched_count: matchedCount,
      unmatched_count: unmatchedCount,
    };
  }, [stockProducts]);

  const applyExtractedInvoiceToForm = useCallback(preview => {
    if (!preview) {
      return;
    }

    const mappedLines = Array.isArray(preview.items) && preview.items.length > 0
      ? preview.items.map(item => ({
          ...createPurchaseLine(),
          product_id: String(item.product_id || ''),
          quantity: String(item.quantity || 1),
          purchase_unit: String(item.purchase_unit || ''),
          purchase_conversion_qty: String(item.purchase_conversion_qty || 1),
          purchase_price: item.purchase_price > 0 ? String(Math.round(item.purchase_price)) : '',
          extracted_description: String(item.description || ''),
          extracted_barcode: String(item.barcode || ''),
          extracted_total: Number(item.total || 0),
          match_confidence: String(item.match_confidence || ''),
          match_score: Number(item.match_score || 0),
          matched_product_name: String(item.matched_product_name || ''),
        }))
      : null;

    setFormData(prev => ({
      ...prev,
      supplier_name: preview.supplier_name || prev.supplier_name,
      invoice_number: preview.invoice_number || prev.invoice_number,
      purchase_date: preview.purchase_date || prev.purchase_date,
      payment_method:
        ['cash', 'transfer', 'qris', 'credit'].includes(preview.payment_method)
          ? preview.payment_method
          : prev.payment_method,
      lines: mappedLines && mappedLines.length > 0 ? mappedLines : prev.lines,
    }));
  }, []);

  const handleInvoiceFileUpload = useCallback(async event => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!selectedOutlet) {
      setInvoiceScanState({
        status: 'error',
        error: 'Pilih outlet lebih dulu sebelum scan invoice supplier.',
        data: null,
      });
      event.target.value = '';
      return;
    }

    if (!authToken || !POS_API_BASE_URL) {
      setInvoiceScanState({
        status: 'error',
        error: 'Endpoint POS belum siap untuk membaca invoice.',
        data: null,
      });
      event.target.value = '';
      return;
    }

    setInvoiceScanState({
      status: 'uploading',
      error: '',
      data: null,
    });

    try {
      const payload = new FormData();
      payload.append('image', file);
      payload.append('outlet_id', String(selectedOutlet));

      const response = await fetch(`${POS_API_BASE_URL}api/purchase/extract-invoice`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken),
        body: payload,
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal membaca invoice supplier.'));
      }

      const preview = hydrateExtractedInvoicePreview(data?.data || data);
      applyExtractedInvoiceToForm(preview);
      setInvoiceScanState({
        status: 'success',
        error: '',
        data: preview,
      });
    } catch (error) {
      console.error('Failed to extract purchase invoice:', error);
      setInvoiceScanState({
        status: 'error',
        error: buildFetchErrorMessage(error, 'Gagal membaca invoice supplier.', {
          backendUrl: POS_API_BASE_URL,
        }),
        data: null,
      });
    } finally {
      event.target.value = '';
    }
  }, [applyExtractedInvoiceToForm, authToken, hydrateExtractedInvoicePreview, selectedOutlet]);

  const buildPayload = useCallback(() => {
    if (!selectedOutlet) {
      throw new Error('Pilih outlet lebih dulu.');
    }

    if (config.key === 'purchase') {
      const items = lines
        .filter(line => Number(line.product_id || 0) > 0 && Number(line.quantity || 0) > 0)
        .map(line => {
          const selectedProduct = productMap[String(line.product_id)];
          return {
            product_id: Number(line.product_id),
            quantity: Number(line.quantity),
            purchase_unit: String(line.purchase_unit || selectedProduct?.purchase_unit || selectedProduct?.satuan || '')
              .trim()
              .toUpperCase(),
            purchase_conversion_qty: Math.max(
              Number(line.purchase_conversion_qty || selectedProduct?.purchase_conversion_qty || 1),
              1
            ),
            purchase_price: Number(line.purchase_price || 0),
            total: Number(line.quantity || 0) * Number(line.purchase_price || 0),
          };
        });

      if (items.length === 0) {
        throw new Error('Tambahkan minimal satu item pembelian.');
      }
      if (!String(formData.supplier_name || '').trim()) {
        throw new Error('Nama supplier wajib diisi.');
      }
      if (String(formData.payment_method || 'cash').trim().toLowerCase() === 'credit' && !formData.due_date) {
        throw new Error('Jatuh tempo wajib diisi untuk purchase tempo.');
      }

      return {
        outlet_id: Number(selectedOutlet),
        supplier_name: String(formData.supplier_name || '').trim(),
        invoice_number: String(formData.invoice_number || '').trim(),
        purchase_date: buildIsoDate(formData.purchase_date),
        due_date:
          String(formData.payment_method || 'cash').trim().toLowerCase() === 'credit' && formData.due_date
            ? buildIsoDate(formData.due_date)
            : null,
        total: computedTotal,
        note: String(formData.note || '').trim(),
        payment_method: String(formData.payment_method || 'cash').trim().toLowerCase(),
        items,
      };
    }

    if (config.key === 'stock-adjustment') {
      const items = lines
        .filter(line => Number(line.product_id || 0) > 0 && Number(line.quantity_delta || 0) !== 0)
        .map(line => ({
          product_id: Number(line.product_id),
          quantity_delta: Number(line.quantity_delta),
          note: String(line.note || '').trim(),
        }));

      if (items.length === 0) {
        throw new Error('Tambahkan minimal satu item adjustment dengan selisih stok.');
      }

      return {
        outlet_id: Number(selectedOutlet),
        adjustment_date: buildIsoDate(formData.adjustment_date),
        reason: String(formData.reason || '').trim(),
        note: String(formData.note || '').trim(),
        items,
      };
    }

    const items = lines
      .filter(line => Number(line.product_id || 0) > 0 && line.actual_stock !== '')
      .map(line => ({
        product_id: Number(line.product_id),
        actual_stock: Number(line.actual_stock),
        note: String(line.note || '').trim(),
      }));

    if (items.length === 0) {
      throw new Error('Tambahkan minimal satu item opname.');
    }

    return {
      outlet_id: Number(selectedOutlet),
      opname_date: buildIsoDate(formData.opname_date),
      note: String(formData.note || '').trim(),
      items,
    };
  }, [computedTotal, config.key, formData.adjustment_date, formData.due_date, formData.invoice_number, formData.note, formData.opname_date, formData.payment_method, formData.purchase_date, formData.reason, formData.supplier_name, lines, productMap, selectedOutlet]);

  const handleSubmit = async event => {
    event.preventDefault();

    setSaving(true);
    try {
      const payload = buildPayload();
      const response = await fetch(`${POS_API_BASE_URL}${config.createUrl}`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(payload),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, `Gagal menyimpan ${config.title}.`));
      }

      await fetchRecords(authToken, selectedOutlet);
      resetForm();
      if (config.key === 'purchase') {
        closeCreateModal();
      }
      Swal.fire('Success', `${config.title} berhasil dicatat.`, 'success');
    } catch (error) {
      console.error(`Failed to save POS ${config.key}:`, error);
      Swal.fire('Error', error.message || `Gagal menyimpan ${config.title}.`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetail = useCallback(async row => {
    if (!config.detailUrl) {
      Swal.fire(
        `Detail ${config.title}`,
        row?.note || 'Dokumen ini tidak memiliki endpoint detail terpisah. Gunakan ringkasan daftar untuk audit cepat.',
        'info'
      );
      return;
    }

    try {
      const response = await fetch(`${POS_API_BASE_URL}${config.detailUrl(row.id)}`, {
        headers: buildAuthHeaders(authToken),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, `Gagal memuat detail ${config.title}.`));
      }

      const detail = data || {};
      const items = Array.isArray(detail.items) ? detail.items : [];
      const rowsHtml = items.length
        ? items.map(item => {
            const name = escapeHtml(item?.product?.name || `Product #${item?.product_id || '-'}`);
            if (config.key === 'stock-adjustment') {
              return `<tr><td>${name}</td><td>${escapeHtml(item.quantity_delta)}</td><td>${escapeHtml(item.stock_before)}</td><td>${escapeHtml(item.stock_after)}</td></tr>`;
            }
            return `<tr><td>${name}</td><td>${escapeHtml(item.system_stock)}</td><td>${escapeHtml(item.actual_stock)}</td><td>${escapeHtml(item.difference_qty)}</td></tr>`;
          }).join('')
        : '<tr><td colspan="4">Belum ada detail item.</td></tr>';

      await Swal.fire({
        title: `${config.title} #${detail.id || row.id}`,
        html: `
          <div style="text-align:left">
            <p><strong>Tanggal:</strong> ${escapeHtml(formatShortDateTime(detail[config.dateField] || row[config.dateField]))}</p>
            <p><strong>Status:</strong> ${escapeHtml(detail.status || '-')}</p>
            <p><strong>Catatan:</strong> ${escapeHtml(detail.note || '-')}</p>
            <div style="overflow:auto">
              <table style="width:100%;border-collapse:collapse">
                <thead>
                  <tr>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Produk</th>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">${config.key === 'stock-adjustment' ? 'Delta' : 'System'}</th>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">${config.key === 'stock-adjustment' ? 'Before' : 'Actual'}</th>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">${config.key === 'stock-adjustment' ? 'After' : 'Difference'}</th>
                  </tr>
                </thead>
                <tbody>${rowsHtml}</tbody>
              </table>
            </div>
          </div>
        `,
        width: 760,
        confirmButtonText: 'Tutup',
      });
    } catch (error) {
      console.error(`Failed to fetch detail for ${config.key}:`, error);
      Swal.fire('Error', error.message || `Gagal memuat detail ${config.title}.`, 'error');
    }
  }, [authToken, config]);

  const columns = useMemo(() => {
    if (config.key === 'purchase') {
      return [
        {
          name: 'Invoice',
          selector: row => row.invoice_number,
          sortable: true,
          grow: 1.15,
          cell: row => (
            <span className="report-center-table__identity">
              <strong>{row.invoice_number || `#${row.id}`}</strong>
              <small>
                {formatShortDateTime(row.purchase_date)}
                {row.due_date ? ` • jatuh tempo ${formatShortDateTime(row.due_date)}` : ''}
              </small>
            </span>
          ),
        },
        {
          name: 'Supplier',
        selector: row => row.supplier_name,
        sortable: true,
        grow: 1.1,
        cell: row => (
          <span className="report-center-table__identity">
            <strong>{row.supplier_name || '-'}</strong>
            <small>
              {(row.payment_method || 'cash').toUpperCase()} • {row.note || 'Tanpa catatan'}
            </small>
          </span>
        ),
      },
      {
        name: 'Payment',
        selector: row => row.payment_status,
        sortable: true,
        width: '190px',
        cell: row => (
          <span className="report-center-table__identity">
            <strong>{String(row.payment_status || 'paid').replace(/_/g, ' ')}</strong>
            <small>
              {Number(row.outstanding_amount || 0) > 0
                ? `Sisa ${formatCurrency(row.outstanding_amount)}`
                : 'Lunas'}
            </small>
          </span>
        ),
      },
      {
        name: 'Total',
        selector: row => Number(row.total || 0),
        sortable: true,
        right: true,
        cell: row => <strong>{formatCurrency(row.total)}</strong>,
      },
        {
          name: 'Sync',
          selector: row => row.accounting_sync_status,
          sortable: true,
          cell: row => (
            <span className={getSyncStatusBadgeClass(row.accounting_sync_status)}>
              {getSyncStatusLabel(row.accounting_sync_status)}
            </span>
          ),
        },
      ];
    }

    return [
      {
        name: 'Dokumen',
        selector: row => row.id,
        sortable: true,
        grow: 1.2,
        cell: row => (
          <span className="report-center-table__identity">
            <strong>#{row.id}</strong>
            <small>{formatShortDateTime(row[config.dateField])}</small>
          </span>
        ),
      },
      {
        name: config.key === 'stock-adjustment' ? 'Reason' : 'Catatan',
        selector: row => row.reason || row.note,
        sortable: true,
        grow: 1.4,
        cell: row => (
          <span className="report-center-table__identity">
            <strong>{config.key === 'stock-adjustment' ? (row.reason || '-') : 'Stock opname'}</strong>
            <small>{row.note || 'Tanpa catatan'}</small>
          </span>
        ),
      },
      {
        name: 'Status',
        selector: row => row.status,
        sortable: true,
        width: '160px',
        cell: row => (
          <span className="pos-catalog-badge pos-catalog-badge--info">
            {String(row.status || 'posted').replace(/_/g, ' ')}
          </span>
        ),
      },
      {
        name: 'Sync',
        selector: row => row.accounting_sync_status,
        sortable: true,
        width: '150px',
        cell: row => (
          <span className={getSyncStatusBadgeClass(row.accounting_sync_status)}>
            {getSyncStatusLabel(row.accounting_sync_status)}
          </span>
        ),
      },
      {
        name: 'Aksi',
        width: '120px',
        cell: row => (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleViewDetail(row)}
          >
            <FaEye className="me-2" />
            Detail
          </Button>
        ),
      },
    ];
  }, [config, handleViewDetail]);

  const meta = (
    <>
      <span className="page-meta__chip">
        <small>Outlet aktif</small>
        <strong>{selectedOutletName}</strong>
      </span>
      <span className="page-meta__chip">
        <small>Total dokumen</small>
        <strong>{summary.totalDocuments}</strong>
      </span>
      {config.key === 'purchase' ? (
        <span className="page-meta__chip">
          <small>Nilai dokumen</small>
          <strong>{formatCurrency(summary.totalAmount)}</strong>
        </span>
      ) : (
        <span className="page-meta__chip">
          <small>Pending sync</small>
          <strong>{summary.pending}</strong>
        </span>
      )}
    </>
  );

  const actions = (
    <>
      <div className="page-action-group">
        <Form.Select
          className="page-action-select"
          value={selectedOutlet}
          onChange={event => setSelectedOutlet(event.target.value)}
          disabled={outlets.length <= 1}
        >
          {outlets.map(outlet => (
            <option key={outlet.id} value={outlet.id}>
              {outlet.name}
            </option>
          ))}
        </Form.Select>
        {config.key !== 'purchase' ? (
          <div className="toolbar-search-shell">
            <FaSearch />
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={`Cari ${config.title.toLowerCase()}...`}
            />
          </div>
        ) : null}
      </div>
      <Button
        variant="outline-secondary"
        onClick={() => authToken && selectedOutlet && fetchRecords(authToken, selectedOutlet)}
      >
        <FaRedoAlt className="me-2" />
        Refresh
      </Button>
      {config.key === 'purchase' ? (
        <Button variant="primary" onClick={openCreateModal}>
          <FaPlus className="me-2" />
          Add Purchase
        </Button>
      ) : null}
    </>
  );

  const renderLineEditor = (line, index) => {
    const selectedProduct = productMap[String(line.product_id)];

    if (config.key === 'purchase') {
      const conversionQty = Math.max(Number(line.purchase_conversion_qty || 1), 1);
      const baseUnitLabel = formatInventoryUnitLabel(selectedProduct?.satuan);
      const purchaseUnitLabel = formatInventoryUnitLabel(line.purchase_unit || selectedProduct?.purchase_unit || selectedProduct?.satuan);
      const unitPurchasePrice = Number(line.purchase_price || 0);
      const lineTotal = Number(line.quantity || 0) * Number(line.purchase_price || 0);
      const stockIncrease = Number(line.quantity || 0) * conversionQty;
      const unitCostInStock = conversionQty > 0 ? unitPurchasePrice / conversionQty : 0;
      const extractedDescription = String(line.extracted_description || '').trim();
      const purchaseProductOptions = buildPurchaseProductSelectOptions(purchaseProductSourceOptions, extractedDescription);
      const selectedProductOption = findPurchaseProductOptionByValue(purchaseProductOptions, line.product_id);
      const scanHint = String(line.extracted_description || '').trim()
        ? `Scan: ${String(line.extracted_description || '').trim()}${line.extracted_barcode ? ` • barcode ${line.extracted_barcode}` : ''}`
        : '';
      return (
        <div key={`purchase-line-${index}`} className="pos-catalog-document-line">
          <div className="pos-catalog-document-line__grid pos-catalog-document-line__grid--purchase">
            <Form.Group>
              <Form.Label>Produk</Form.Label>
              <Select
                inputId={`purchase-product-select-${index}`}
                className="pos-catalog-product-select"
                classNamePrefix="pos-catalog-product-select"
                styles={purchaseProductSelectStyles}
                menuPortalTarget={purchaseProductMenuPortalTarget}
                menuPlacement="auto"
                isClearable
                options={purchaseProductOptions}
                value={selectedProductOption}
                placeholder={
                  extractedDescription
                    ? `Cari product untuk "${truncatePurchasePreviewText(extractedDescription, 32)}"`
                    : 'Pilih product stok'
                }
                filterOption={filterPurchaseProductOption}
                noOptionsMessage={() => 'Tidak ada product yang cocok.'}
                formatOptionLabel={(option, { context }) => {
                  if (context === 'value') {
                    return option.label;
                  }

                  return (
                    <div className="pos-catalog-product-option">
                      <div className="pos-catalog-product-option__head">
                        <strong>{option.label}</strong>
                        {extractedDescription ? (
                          <span className={getPurchaseMatchBadgeClass(option.ocrConfidence)}>
                            {getPurchaseMatchLabel(option.ocrConfidence)}
                          </span>
                        ) : null}
                      </div>
                      <div className="pos-catalog-product-option__meta">
                        <span>
                          {option.product?.code ? `Kode ${option.product.code}` : 'Tanpa kode'}
                          {option.product?.barcode ? ` • barcode ${option.product.barcode}` : ''}
                        </span>
                        <span>
                          Stok {Number(option.product?.stock || 0).toLocaleString('id-ID')} {formatInventoryUnitLabel(option.product?.satuan)}
                        </span>
                      </div>
                    </div>
                  );
                }}
                onChange={selectedOption => handleLineChange(index, 'product_id', selectedOption?.value || '')}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Qty beli</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={line.quantity}
                onChange={event => handleLineChange(index, 'quantity', event.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Unit beli</Form.Label>
              <Form.Control
                value={line.purchase_unit || ''}
                onChange={event => handleLineChange(index, 'purchase_unit', event.target.value)}
                placeholder="KG / PACK / LITER"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Konversi stok</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={line.purchase_conversion_qty || '1'}
                onChange={event => handleLineChange(index, 'purchase_conversion_qty', event.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Harga beli / unit</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={line.purchase_price}
                onChange={event => handleLineChange(index, 'purchase_price', event.target.value)}
              />
            </Form.Group>
            <Button
              variant="outline-danger"
              type="button"
              onClick={() => removeLine(index)}
            >
              <FaTrash />
            </Button>
          </div>
          <div className="pos-catalog-document-line__meta">
            <span>
              {selectedProduct
                ? `${getItemTypeLabel(selectedProduct.item_type)} • stok ${Number(selectedProduct.stock || 0).toLocaleString('id-ID')} ${baseUnitLabel} • beli ${Number(line.quantity || 0).toLocaleString('id-ID')} ${purchaseUnitLabel} = masuk ${stockIncrease.toLocaleString('id-ID')} ${baseUnitLabel}${scanHint ? ` • ${scanHint}` : ''}`
                : scanHint || 'Pilih product lebih dulu'}
            </span>
            <div className="pos-catalog-document-line__meta-group">
              {line.match_confidence ? (
                <span className={getPurchaseMatchBadgeClass(line.match_confidence)}>
                  {getPurchaseMatchLabel(line.match_confidence)}
                </span>
              ) : null}
              <strong>
                {formatCurrency(lineTotal)} • HPP dasar {formatCurrency(unitCostInStock)} / {baseUnitLabel}
              </strong>
            </div>
          </div>
        </div>
      );
    }

    if (config.key === 'stock-adjustment') {
      const stockBefore = Number(selectedProduct?.stock || 0);
      const stockAfter = stockBefore + Number(line.quantity_delta || 0);
      return (
        <div key={`adjustment-line-${index}`} className="pos-catalog-document-line">
          <div className="pos-catalog-document-line__grid">
            <Form.Group>
              <Form.Label>Produk</Form.Label>
              <Form.Select
                value={line.product_id}
                onChange={event => handleLineChange(index, 'product_id', event.target.value)}
              >
                <option value="">Pilih produk stok</option>
                {stockProducts.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Delta</Form.Label>
              <Form.Control
                type="number"
                value={line.quantity_delta}
                onChange={event => handleLineChange(index, 'quantity_delta', event.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Catatan item</Form.Label>
              <Form.Control
                value={line.note}
                onChange={event => handleLineChange(index, 'note', event.target.value)}
                placeholder="Alasan item"
              />
            </Form.Group>
            <Button
              variant="outline-danger"
              type="button"
              onClick={() => removeLine(index)}
            >
              <FaTrash />
            </Button>
          </div>
          <div className="pos-catalog-document-line__meta">
            <span>
              {selectedProduct ? `Stok ${stockBefore} -> ${stockAfter}` : 'Pilih product lebih dulu'}
            </span>
            {selectedProduct ? (
              <span className={getItemTypeBadgeClass(selectedProduct.item_type)}>
                {getItemTypeLabel(selectedProduct.item_type)}
              </span>
            ) : null}
          </div>
        </div>
      );
    }

    const systemStock = Number(selectedProduct?.stock || 0);
    const actualStock = Number(line.actual_stock || 0);
    const difference = actualStock - systemStock;

    return (
      <div key={`opname-line-${index}`} className="pos-catalog-document-line">
        <div className="pos-catalog-document-line__grid pos-catalog-document-line__grid--opname">
          <Form.Group>
            <Form.Label>Produk</Form.Label>
            <Form.Select
              value={line.product_id}
              onChange={event => handleLineChange(index, 'product_id', event.target.value)}
            >
              <option value="">Pilih produk stok</option>
              {stockProducts.map(product => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Stok sistem</Form.Label>
            <Form.Control value={selectedProduct ? systemStock : '-'} disabled />
          </Form.Group>
          <Form.Group>
            <Form.Label>Stok aktual</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={line.actual_stock}
              onChange={event => handleLineChange(index, 'actual_stock', event.target.value)}
            />
          </Form.Group>
          <Button
            variant="outline-danger"
            type="button"
            onClick={() => removeLine(index)}
          >
            <FaTrash />
          </Button>
        </div>
        <div className="pos-catalog-document-line__meta">
          <span>
            {selectedProduct ? `Selisih ${difference > 0 ? '+' : ''}${difference}` : 'Pilih product lebih dulu'}
          </span>
          <Form.Control
            value={line.note}
            onChange={event => handleLineChange(index, 'note', event.target.value)}
            placeholder="Catatan item opname"
          />
        </div>
      </div>
    );
  };

  const quickGuideCard = (
    <div className="surface-card pos-catalog-note-card">
      <span className="page-eyebrow">Panduan cepat</span>
      <h2>{config.noteTitle}</h2>
      <p>{config.noteCopy}</p>

      <div className="pos-catalog-inline-list">
        {(config.workflow || []).map(step => (
          <article key={step.label} className="pos-catalog-inline-item">
            <div className="pos-catalog-value-stack">
              <strong>{step.label}</strong>
              <small>{step.value}</small>
            </div>
          </article>
        ))}
      </div>
    </div>
  );

  const renderDocumentFormCard = ({ inModal = false } = {}) => (
    <section className={`surface-card pos-catalog-form-card${inModal ? ' pos-catalog-form-card--modal' : ''}`}>
      <div className="pos-catalog-form-head">
        <div>
          <span className="page-eyebrow">{inModal ? 'Popup entry' : 'Input Dokumen'}</span>
          <h2>{inModal ? `Tambah ${config.title}` : `Catat ${config.title}`}</h2>
          <p>Outlet aktif: {selectedOutletName}</p>
        </div>
        <Button variant="outline-secondary" type="button" onClick={resetForm}>
          Reset
        </Button>
      </div>

      {inModal && config.key === 'purchase' ? (
        <div className="pos-catalog-purchase-modal-hero">
          <div className="pos-catalog-purchase-modal-hero__copy">
            <strong>Form purchase dipisah ke popup supaya halaman utama fokus ke audit dokumen.</strong>
            <small>Isi supplier, invoice, item beli, lalu simpan. Setelah itu daftar purchase akan refresh otomatis.</small>
          </div>
          <div className="pos-catalog-purchase-modal-hero__stats">
            <span className="pos-catalog-filter-chip">Outlet {getInitials(selectedOutletName)}</span>
            <span className="pos-catalog-filter-chip">{lines.length} item aktif</span>
            <span className="pos-catalog-filter-chip">{formatCurrency(computedTotal)}</span>
          </div>
        </div>
      ) : null}

      <Form onSubmit={handleSubmit} className="d-grid gap-3">
        {config.key === 'purchase' ? (
          <>
            <div className="pos-catalog-subsection pos-catalog-invoice-scan">
              <div className="pos-catalog-invoice-scan__head">
                <div>
                  <h3>Scan invoice supplier</h3>
                  <p className="text-muted mb-0">
                    Upload foto invoice atau struk supplier. Sistem akan baca supplier, tanggal, total, dan item lalu mencoba mencocokkan ke product stok outlet aktif.
                  </p>
                </div>
                <Form.Group className="pos-catalog-invoice-scan__picker">
                  <Form.Label className="mb-1">Upload invoice</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleInvoiceFileUpload}
                    disabled={saving || invoiceScanState.status === 'uploading'}
                  />
                </Form.Group>
              </div>

              {invoiceScanState.status === 'uploading' ? (
                <Alert variant="info" className="mb-0">
                  <FaFileUpload className="me-2" />
                  Sedang membaca invoice supplier. Hasil scan akan langsung mengisi form purchase setelah selesai.
                </Alert>
              ) : null}

              {invoiceScanState.status === 'error' && invoiceScanState.error ? (
                <Alert variant="warning" className="mb-0">
                  {invoiceScanState.error}
                </Alert>
              ) : null}

              {invoiceScanState.data ? (
                <>
                  <div className="pos-catalog-mini-grid">
                    <article className="pos-catalog-inline-item pos-catalog-inline-item--soft">
                      <div className="pos-catalog-value-stack">
                        <strong>Header invoice</strong>
                        <span>
                          {invoiceScanState.data.supplier_name || 'Supplier belum terbaca'}
                          {invoiceScanState.data.invoice_number ? ` • ${invoiceScanState.data.invoice_number}` : ''}
                        </span>
                      </div>
                      <span className="pos-catalog-filter-chip">
                        {invoiceScanState.data.provider || 'OCR'}
                      </span>
                    </article>
                    <article className="pos-catalog-inline-item">
                      <div className="pos-catalog-value-stack">
                        <strong>Ringkasan hasil scan</strong>
                        <span>
                          {invoiceScanState.data.matched_count} match otomatis • {invoiceScanState.data.unmatched_count} perlu cek manual
                        </span>
                      </div>
                      <strong>{invoiceScanState.data.total > 0 ? formatCurrency(invoiceScanState.data.total) : 'Tanpa total'}</strong>
                    </article>
                  </div>

                  {invoiceScanState.data.warnings.length > 0 ? (
                    <Alert variant="warning" className="mb-0">
                      {invoiceScanState.data.warnings.join(' ')}
                    </Alert>
                  ) : null}

                  {invoiceScanState.data.items.length > 0 ? (
                    <div className="pos-catalog-inline-list pos-catalog-invoice-scan__items">
                      {invoiceScanState.data.items.map((item, index) => (
                        <article key={`invoice-scan-item-${index}`} className="pos-catalog-inline-item">
                          <div className="pos-catalog-value-stack">
                            <strong>{item.description || `Item #${index + 1}`}</strong>
                            <span>
                              {Number(item.quantity || 0).toLocaleString('id-ID')} x {formatCurrency(item.purchase_price || 0)}
                              {item.barcode ? ` • barcode ${item.barcode}` : ''}
                            </span>
                            <span>
                              {item.matched_product_name
                                ? `Masuk ke product: ${item.matched_product_name}`
                                : 'Belum ketemu product yang cukup yakin. Pilih manual di baris item.'}
                            </span>
                          </div>
                          <span className={getPurchaseMatchBadgeClass(item.match_confidence)}>
                            {getPurchaseMatchLabel(item.match_confidence)}
                          </span>
                        </article>
                      ))}
                    </div>
                  ) : null}

                  {invoiceScanState.data.raw_text ? (
                    <Form.Group>
                      <Form.Label>Hasil OCR mentah</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        value={invoiceScanState.data.raw_text}
                        readOnly
                      />
                    </Form.Group>
                  ) : null}
                </>
              ) : null}
            </div>

            <div className="pos-catalog-mini-grid">
              <Form.Group>
                <Form.Label>Supplier</Form.Label>
                <Form.Control
                  name="supplier_name"
                  value={formData.supplier_name}
                  onChange={handleFieldChange}
                  placeholder="Nama supplier / vendor barang"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Invoice</Form.Label>
                <Form.Control
                  name="invoice_number"
                  value={formData.invoice_number}
                  onChange={handleFieldChange}
                  placeholder="INV-..."
                />
              </Form.Group>
            </div>

            <div className="pos-catalog-mini-grid">
              <Form.Group>
                <Form.Label>Tanggal pembelian</Form.Label>
                <Form.Control
                  type="date"
                  name="purchase_date"
                  value={formData.purchase_date}
                  onChange={handleFieldChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Metode bayar</Form.Label>
                <Form.Select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleFieldChange}
                >
                  <option value="cash">Cash</option>
                  <option value="transfer">Transfer</option>
                  <option value="qris">QRIS</option>
                  <option value="credit">Tempo / Hutang Vendor</option>
                </Form.Select>
                <div className="pos-catalog-field-note">
                  Jika pilih tempo, sistem akan otomatis membuat hutang vendor inventory yang nanti dibayar dari menu Vendors.
                </div>
              </Form.Group>
            </div>

            {String(formData.payment_method || '').toLowerCase() === 'credit' ? (
              <Form.Group>
                <Form.Label>Jatuh tempo</Form.Label>
                <Form.Control
                  type="date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleFieldChange}
                />
                <div className="pos-catalog-field-note">
                  Wajib diisi agar purchase tempo masuk ke daftar outstanding vendor dengan tanggal jatuh tempo yang jelas.
                </div>
              </Form.Group>
            ) : null}
          </>
        ) : config.key === 'stock-adjustment' ? (
          <div className="pos-catalog-mini-grid">
            <Form.Group>
              <Form.Label>Tanggal adjustment</Form.Label>
              <Form.Control
                type="date"
                name="adjustment_date"
                value={formData.adjustment_date}
                onChange={handleFieldChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Reason</Form.Label>
              <Form.Select
                name="reason"
                value={formData.reason}
                onChange={handleFieldChange}
              >
                <option value="manual_adjustment">Manual Adjustment</option>
                <option value="damaged">Damaged</option>
                <option value="lost">Lost</option>
                <option value="found">Found</option>
                <option value="expired">Expired</option>
              </Form.Select>
            </Form.Group>
          </div>
        ) : (
          <Form.Group>
            <Form.Label>Tanggal opname</Form.Label>
            <Form.Control
              type="date"
              name="opname_date"
              value={formData.opname_date}
              onChange={handleFieldChange}
            />
          </Form.Group>
        )}

        <Form.Group>
          <Form.Label>Catatan dokumen</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="note"
            value={formData.note}
            onChange={handleFieldChange}
            placeholder={`Catatan ${config.title.toLowerCase()}...`}
          />
        </Form.Group>

        <div className="pos-catalog-document-lines">
          {lines.map(renderLineEditor)}
        </div>

        <div className="d-flex justify-content-between flex-wrap gap-2">
          <Button variant="outline-secondary" type="button" onClick={addLine}>
            <FaPlus className="me-2" />
            Tambah item
          </Button>
          {config.key === 'purchase' ? (
            <div className="pos-catalog-document-total">
              <span>Total pembelian</span>
              <strong>{formatCurrency(computedTotal)}</strong>
            </div>
          ) : null}
        </div>

        <div className="pos-catalog-form-actions">
          <div className="pos-catalog-form-actions__group">
            {inModal ? (
              <Button variant="outline-secondary" type="button" onClick={closeCreateModal}>
                Tutup
              </Button>
            ) : null}
            <Button variant="outline-secondary" type="button" onClick={resetForm}>
              Reset
            </Button>
          </div>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Menyimpan...' : `Simpan ${config.title}`}
          </Button>
        </div>
      </Form>
    </section>
  );

  return (
    <Fragment>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={config.icon}
          eyebrow={config.eyebrow}
          title={config.title}
          description={config.description}
          meta={meta}
          actions={actions}
          breadcrumbs={[
            { label: 'Catalog & Inventory' },
            { label: config.title },
          ]}
        />

        <div className="pos-catalog-shell">
          {!POS_API_BASE_URL ? (
            <Alert variant="danger" className="section-card">
              Endpoint POS belum diatur. Pastikan `REACT_APP_API_PRODUCT_URL` tersedia di konfigurasi `adminfitness`.
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="warning" className="section-card">
              {errorMessage}
            </Alert>
          ) : null}

          <div className="pos-catalog-kpi-grid">
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaBoxes />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Total dokumen</p>
                <strong>{summary.totalDocuments}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaClipboardCheck />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Synced</p>
                <strong>{summary.synced}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaRedoAlt />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Pending</p>
                <strong>{summary.pending}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-note-card">
              <span className="page-eyebrow">{config.key === 'purchase' ? 'Nilai Pembelian' : 'Failed Sync'}</span>
              <h2>{config.key === 'purchase' ? formatCurrency(summary.totalAmount) : summary.failed}</h2>
              <p>{config.key === 'purchase' ? 'Total nilai dokumen pembelian dalam filter aktif.' : 'Jumlah dokumen yang gagal sinkron ke accounting.'}</p>
            </article>
          </div>

          {config.key === 'purchase' ? (
            <>
              <section className="surface-card table-card pos-catalog-table-card pos-catalog-purchase-list-card">
                <div className="pos-catalog-toolbar">
                  <div>
                    <span className="page-eyebrow">Riwayat Dokumen</span>
                    <h2 className="mb-1">Dokumen {config.title}</h2>
                    <p className="text-muted mb-0">Audit semua dokumen outlet aktif, lalu tambah purchase baru lewat popup agar workspace tetap rapi.</p>
                  </div>

                  <div className="pos-catalog-toolbar__meta pos-catalog-toolbar__meta--split">
                    <div className="pos-catalog-toolbar__group">
                      <span className="pos-catalog-filter-chip">
                        Outlet {getInitials(selectedOutletName)}
                      </span>
                      <span className="pos-catalog-filter-chip">
                        {filteredRecords.length} baris
                      </span>
                      <span className="pos-catalog-filter-chip">
                        {formatCurrency(summary.totalAmount)}
                      </span>
                    </div>
                    <div className="pos-catalog-toolbar__group pos-catalog-toolbar__group--right">
                      <div className="toolbar-search-shell pos-catalog-toolbar__search">
                        <FaSearch />
                        <input
                          type="search"
                          value={search}
                          onChange={event => setSearch(event.target.value)}
                          placeholder="Cari invoice, supplier, note, atau payment..."
                        />
                      </div>
                      <Form.Control
                        type="date"
                        value={dateRange.start_date}
                        onChange={event =>
                          setDateRange(prev => ({ ...prev, start_date: event.target.value }))
                        }
                      />
                      <Form.Control
                        type="date"
                        value={dateRange.end_date}
                        onChange={event =>
                          setDateRange(prev => ({ ...prev, end_date: event.target.value }))
                        }
                      />
                      <Button variant="primary" className="pos-catalog-list-cta" onClick={openCreateModal}>
                        <FaPlus className="me-2" />
                        Add Purchase
                      </Button>
                    </div>
                  </div>
                </div>

                <AdminDataTable
                  columns={columns}
                  data={filteredRecords}
                  progressPending={loading}
                  pagination
                  highlightOnHover
                  emptyTitle={`Belum ada ${config.title}`}
                  emptyDescription={`Dokumen ${config.title.toLowerCase()} akan muncul di sini setelah outlet mulai melakukan pencatatan.`}
                />
              </section>

              {quickGuideCard}
            </>
          ) : (
            <>
              {quickGuideCard}

              <div className="pos-catalog-layout">
                {renderDocumentFormCard()}

                <section className="surface-card table-card pos-catalog-table-card">
                  <div className="pos-catalog-toolbar">
                    <div>
                      <span className="page-eyebrow">Riwayat Dokumen</span>
                      <h2 className="mb-1">Dokumen {config.title}</h2>
                      <p className="text-muted mb-0">Audit semua dokumen outlet aktif dari filter periode yang dipilih.</p>
                    </div>

                    <div className="pos-catalog-toolbar__meta">
                      <span className="pos-catalog-filter-chip">
                        Outlet {getInitials(selectedOutletName)}
                      </span>
                      <span className="pos-catalog-filter-chip">
                        {filteredRecords.length} baris
                      </span>
                      <Form.Control
                        type="date"
                        value={dateRange.start_date}
                        onChange={event =>
                          setDateRange(prev => ({ ...prev, start_date: event.target.value }))
                        }
                      />
                      <Form.Control
                        type="date"
                        value={dateRange.end_date}
                        onChange={event =>
                          setDateRange(prev => ({ ...prev, end_date: event.target.value }))
                        }
                      />
                    </div>
                  </div>

                  <AdminDataTable
                    columns={columns}
                    data={filteredRecords}
                    progressPending={loading}
                    pagination
                    highlightOnHover
                    emptyTitle={`Belum ada ${config.title}`}
                    emptyDescription={`Dokumen ${config.title.toLowerCase()} akan muncul di sini setelah outlet mulai melakukan pencatatan.`}
                  />
                </section>
              </div>
            </>
          )}

          {config.key === 'purchase' ? (
            <Modal
              show={showCreateModal}
              onHide={closeCreateModal}
              size="xl"
              centered
              scrollable
              dialogClassName="pos-catalog-purchase-modal"
            >
              <Modal.Header closeButton>
                <Modal.Title>Tambah Purchase</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {renderDocumentFormCard({ inModal: true })}
              </Modal.Body>
            </Modal>
          ) : null}
        </div>
      </Container>
    </Fragment>
  );
}

export default PosInventoryDocumentManager;
